import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Pencil, Trash2, TrendingUp, Star } from 'lucide-react';

export function TemplateManagerDialog({
  open, onOpenChange, customTemplates, usageStats,
  editingTemplate, templateForm, setTemplateForm,
  onSave, onDelete, onStartEdit, onResetForm,
  ICON_MAP, ICON_OPTIONS, COLOR_OPTIONS, APP_MODULES,
  ALL_MODULES, PERMISSION_GROUPS
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) onResetForm(); }}>
      <DialogContent className="max-w-4xl max-h-[90dvh] overflow-y-auto" aria-describedby="template-manager-description">
        <DialogHeader>
          <DialogTitle className="font-outfit">{editingTemplate ? 'Edit Template' : 'Template Manager'}</DialogTitle>
          <p id="template-manager-description" className="text-sm text-muted-foreground">Create and manage custom role templates for quick user setup</p>
        </DialogHeader>

        {!editingTemplate && customTemplates.length > 0 && (
          <div className="space-y-3">
            <Label className="text-sm font-semibold text-muted-foreground">Your Custom Templates</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="custom-templates-list">
              {customTemplates.map((tmpl) => {
                const Icon = ICON_MAP[tmpl.icon] || Star;
                return (
                  <div key={tmpl.id} className="flex items-center gap-3 p-3 rounded-xl border border-border bg-white hover:bg-muted/50 transition-all" data-testid={`custom-template-${tmpl.id}`}>
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${tmpl.color || 'from-blue-500 to-indigo-600'} flex items-center justify-center shrink-0`}>
                      <Icon size={18} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{tmpl.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{tmpl.description || 'No description'} &middot; {tmpl.role || 'operator'}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {(usageStats[tmpl.id]?.usage_count || 0) > 0 && (
                        <span className="text-[10px] text-muted-foreground bg-muted px-2 py-1 rounded-lg flex items-center gap-1" data-testid={`tmpl-stats-${tmpl.id}`}>
                          <TrendingUp size={10} /> {usageStats[tmpl.id].usage_count}x
                        </span>
                      )}
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => onStartEdit(tmpl)} data-testid={`edit-template-${tmpl.id}`}>
                        <Pencil size={14} className="text-muted-foreground" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => onDelete(tmpl.id)} data-testid={`delete-template-${tmpl.id}`}>
                        <Trash2 size={14} className="text-error" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="border-t border-border pt-4">
              <Label className="text-sm font-semibold text-muted-foreground">Create New Template</Label>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Template Name *</Label>
              <Input value={templateForm.name} onChange={(e) => setTemplateForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Kitchen Lead" data-testid="template-name-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={templateForm.description} onChange={(e) => setTemplateForm(p => ({ ...p, description: e.target.value }))} placeholder="Brief description" data-testid="template-description-input" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Role *</Label>
              <Select value={templateForm.role} onValueChange={(val) => setTemplateForm(p => ({ ...p, role: val }))}>
                <SelectTrigger data-testid="template-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="operator">Operator</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Icon</Label>
              <Select value={templateForm.icon} onValueChange={(val) => setTemplateForm(p => ({ ...p, icon: val }))}>
                <SelectTrigger data-testid="template-icon-select">
                  <div className="flex items-center gap-2">
                    {(() => { const I = ICON_MAP[templateForm.icon] || Star; return <I size={14} />; })()}
                    <span className="text-xs">{templateForm.icon}</span>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {ICON_OPTIONS.map((name) => { const I = ICON_MAP[name]; return <SelectItem key={name} value={name}><span className="flex items-center gap-2"><I size={14} /> {name}</span></SelectItem>; })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Color</Label>
              <Select value={templateForm.color} onValueChange={(val) => setTemplateForm(p => ({ ...p, color: val }))}>
                <SelectTrigger data-testid="template-color-select">
                  <div className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded bg-gradient-to-br ${templateForm.color}`} />
                    <span className="text-xs">{COLOR_OPTIONS.find(c => c.value === templateForm.color)?.label || 'Custom'}</span>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {COLOR_OPTIONS.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      <span className="flex items-center gap-2"><div className={`w-4 h-4 rounded bg-gradient-to-br ${c.value}`} />{c.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-sm font-semibold mb-2 block">Module Access</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {APP_MODULES.map((mod) => {
                const isEnabled = (templateForm.allowed_modules || []).includes(mod.id);
                return (
                  <div key={mod.id} className={`flex items-center justify-between p-2.5 rounded-lg border cursor-pointer transition-all text-sm ${isEnabled ? `${mod.color} border-current/20` : 'bg-muted/50 text-muted-foreground border-border'}`}
                    onClick={() => setTemplateForm(p => { const c = p.allowed_modules || []; return { ...p, allowed_modules: c.includes(mod.id) ? c.filter(m => m !== mod.id) : [...c, mod.id] }; })}
                    data-testid={`tmpl-module-${mod.id}`}>
                    <span className="font-medium">{mod.label}</span>
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${isEnabled ? 'bg-primary border-primary' : 'bg-white border-border'}`}>
                      {isEnabled && <span className="text-white text-[8px] font-bold">&#10003;</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-sm font-semibold mb-2 block">Permissions</Label>
            <div className="border rounded-xl p-3 bg-muted/50 space-y-3 max-h-56 overflow-y-auto">
              {PERMISSION_GROUPS.map(group => (
                <div key={group}>
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1.5">{group}</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {ALL_MODULES.filter(m => m.group === group).map((mod) => {
                      const currentLevel = (templateForm.permissions || {})[mod.value] || 'none';
                      return (
                        <div key={mod.value} className="flex items-center justify-between p-1.5 rounded bg-white border border-border">
                          <span className="text-xs font-medium">{mod.label}</span>
                          <div className="flex rounded overflow-hidden border border-border">
                            {[{ val: 'write', label: 'W', cls: 'bg-success text-white' }, { val: 'read', label: 'R', cls: 'bg-info text-white' }, { val: 'none', label: '\u2014', cls: 'bg-stone-400 text-white' }].map(opt => (
                              <button key={opt.val} type="button" onClick={() => setTemplateForm(p => ({ ...p, permissions: { ...p.permissions, [mod.value]: opt.val } }))}
                                className={`w-6 h-6 text-[9px] font-bold transition-all ${currentLevel === opt.val ? opt.cls : 'bg-white text-muted-foreground hover:bg-muted/50'}`}>
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" onClick={onSave} className="rounded-full" data-testid="save-template-button">
              {editingTemplate ? 'Update Template' : 'Create Template'}
            </Button>
            {editingTemplate && <Button type="button" variant="outline" className="rounded-full" onClick={onResetForm}>Cancel Edit</Button>}
            <Button type="button" variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>Close</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
